<template>
  <v-dialog v-model="dialog" max-width="850px" persistent @keydown.esc="close">
    <v-card>
      <v-toolbar dark color="primary">
        <v-icon class="mr-2">event_note</v-icon>
        <v-toolbar-title>{{ mode === 'create' ? 'Nueva Reserva' : 'Editar Reserva' }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon color="red" @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <v-form ref="form" v-model="formValid">
          <v-layout row wrap>

            <!-- Columna izquierda -->
            <v-flex xs12 md6 class="pr-3">
              <p class="subheading grey--text mb-2">Empresa y período</p>

              <v-autocomplete
                v-model="formData.beneficiary_company_id"
                :items="companies"
                item-text="name"
                item-value="id"
                label="Empresa beneficiaria"
                prepend-icon="business"
                :rules="[rules.required]"
                clearable
              ></v-autocomplete>

              <!-- Checkbox: Tipo de Reserva -->
              <div class="mb-2">
                <v-checkbox
                  v-model="formData.reserveByHours"
                  label="Reservar por horario específico"
                  color="primary"
                  class="my-0"
                ></v-checkbox>
                <p class="caption grey--text mt-1 mb-0">
                  {{ formData.reserveByHours ? 'Define horarios de entrada y salida' : 'Reserva para todo el día' }}
                </p>
              </div>

              <v-menu ref="startMenu" v-model="startMenu" :close-on-content-click="false" offset-y lazy>
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="formData.start_date"
                    label="Fecha inicio"
                    prepend-icon="event"
                    readonly
                    :rules="[rules.required]"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="formData.start_date" @input="startMenu = false"></v-date-picker>
              </v-menu>

              <v-menu ref="endMenu" v-model="endMenu" :close-on-content-click="false" offset-y lazy>
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="formData.end_date"
                    label="Fecha fin"
                    prepend-icon="event"
                    readonly
                    :rules="[rules.required, rules.endDateAfterStart]"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker v-model="formData.end_date" @input="endMenu = false"></v-date-picker>
              </v-menu>

              <!-- Time pickers: Solo se muestran si es reserva por horas -->
              <v-layout row wrap v-if="formData.reserveByHours">
                <v-flex xs12 sm6 class="pr-2">
                  <v-text-field
                    v-model="formData.start_time"
                    label="Hora inicio"
                    type="time"
                    prepend-icon="access_time"
                    :rules="[rules.requiredIfByHours]"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 class="pl-2">
                  <v-text-field
                    v-model="formData.end_time"
                    label="Hora fin"
                    type="time"
                    prepend-icon="access_time"
                    :rules="[rules.requiredIfByHours, rules.endTimeAfterStart]"
                  ></v-text-field>
                </v-flex>
              </v-layout>

              <v-textarea
                v-model="formData.notes"
                label="Notas (opcional)"
                prepend-icon="note"
                rows="3"
                counter="256"
              ></v-textarea>
            </v-flex>

            <!-- Columna derecha -->
            <v-flex xs12 md6 class="pl-3">
              <p class="subheading grey--text mb-2">Cajones de parqueo</p>

              <v-autocomplete
                v-model="formData.parking_space_ids"
                :items="availableSpaces"
                item-text="display_name"
                item-value="id"
                label="Seleccionar cajones"
                prepend-icon="local_parking"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                :loading="loadingSpaces"
                :disabled="loadingSpaces"
              ></v-autocomplete>

              <p v-if="availableSpaces.length === 0 && !loadingSpaces" class="caption orange--text">
                <v-icon small>warning</v-icon>
                No hay espacios disponibles en tu empresa
              </p>

              <!-- Lista de cajones seleccionados -->
              <v-list two-line dense v-if="selectedSpaces.length > 0">
                <v-subheader>Cajones seleccionados ({{ selectedSpaces.length }})</v-subheader>
                <v-list-tile v-for="(space, i) in selectedSpaces" :key="'space-' + space.id">
                  <v-list-tile-action>
                    <v-icon small color="primary">local_parking</v-icon>
                  </v-list-tile-action>
                  <v-list-tile-content>
                    <v-list-tile-title>Cajón {{ space.number }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ space.space_type_name }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                  <v-list-tile-action>
                    <v-btn icon small @click="removeSpace(space.id)">
                      <v-icon small color="red">delete</v-icon>
                    </v-btn>
                  </v-list-tile-action>
                </v-list-tile>
                <v-divider v-for="i in selectedSpaces.length - 1" :key="'divider-' + i"></v-divider>
              </v-list>

              <v-alert v-if="selectedSpaces.length === 0" :value="true" color="info" icon="info" outline>
                Selecciona al menos un cajón
              </v-alert>
            </v-flex>

          </v-layout>

          <v-alert v-if="error" type="warning" outline transition="scale-transition">
            {{ error }}
          </v-alert>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-btn color="error" @click="close">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="loading" @click="submit">
          {{ mode === 'create' ? 'Crear Reserva' : 'Guardar Cambios' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { mapGetters } from 'vuex'
import { handleApiError, extractErrorMessage } from '@/utils/errorHandler'

export default {
  name: 'ReservationDialog',
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    mode: {
      type: String,
      default: 'create',
      validator: (v) => ['create', 'edit'].includes(v)
    },
    companies: {
      type: Array,
      default: () => []
    },
    parkingSpaces: {
      type: Array,
      default: () => []
    },
    initialData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formValid: false,
      startMenu: false,
      endMenu: false,
      loadingSpaces: false,
      availableParkingSpaces: [],
      filterBySection: null,
      sections: [],
      formData: {
        beneficiary_company_id: null,
        parking_space_ids: [],
        start_date: '',
        end_date: '',
        notes: '',
        reserveByHours: false,
        start_time: '',
        end_time: ''
      },
      loading: false,
      error: null,
      rules: {
        required: (v) => !!v || 'Campo requerido',
        requiredIfByHours: (v) => {
          if (!this.formData.reserveByHours) return true
          return !!v || 'Hora requerida'
        },
        endDateAfterStart: (v) => {
          if (!v || !this.formData.start_date) return true
          return v >= this.formData.start_date || 'La fecha fin debe ser mayor o igual a la inicio'
        },
        endTimeAfterStart: (v) => {
          if (!this.formData.reserveByHours || !v || !this.formData.start_time) return true
          return v > this.formData.start_time || 'La hora fin debe ser mayor a la hora inicio'
        }
      }
    }
  },
  computed: {
    ...mapGetters(['getProfile']),

    selectedSpaces() {
      return this.formData.parking_space_ids
        .map(id => this.availableParkingSpaces.find(s => s.id === id))
        .filter(s => s)
    },

    availableSpaces() {
      return this.availableParkingSpaces.map(s => ({
        ...s,
        display_name: 'Cajón ' + s.number + ' - ' + (s.space_type_name || 'Sin tipo')
      }))
    }
  },
  watch: {
    dialog(val) {
      if (val) {
        this.initializeForm()
        this.loadAvailableSpaces()
      }
    }
  },
  methods: {
    async loadAvailableSpaces() {
      if (!this.getProfile || !this.getProfile.id_company) {
        console.warn('No company ID found')
        return
      }

      this.loadingSpaces = true
      try {
        // Obtener espacios disponibles de la empresa dueña
        const params = {
          id_section__idEmpresa: this.getProfile.id_company,
          status: 'available'
        }

        const response = await axios({
          method: 'get',
          url: 'https://empresa-service.intrare.app/Parking/spaces/',
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + store.getters.getToken
          },
          params: params
        })

        this.availableParkingSpaces = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error('Error al cargar espacios disponibles:', error)
        handleApiError(error, store)
        this.availableParkingSpaces = []
      } finally {
        this.loadingSpaces = false
      }
    },

    initializeForm() {
      this.error = null
      this.startMenu = false
      this.endMenu = false

      if (this.mode === 'edit' && this.initialData && this.initialData.id) {
        this.formData = {
          beneficiary_company_id: this.initialData.beneficiary_company,
          parking_space_ids: this.initialData.reserved_spaces.map(s => s.parking_space),
          start_date: this.initialData.start_date,
          end_date: this.initialData.end_date,
          notes: this.initialData.notes || '',
          reserveByHours: !!(this.initialData.start_time || this.initialData.end_time),
          start_time: this.initialData.start_time || '',
          end_time: this.initialData.end_time || ''
        }
      } else {
        const today = new Date().toISOString().split('T')[0]
        this.formData = {
          beneficiary_company_id: null,
          parking_space_ids: [],
          start_date: today,
          end_date: today,
          notes: '',
          reserveByHours: false,
          start_time: '08:00',
          end_time: '18:00'
        }
      }
    },

    removeSpace(spaceId) {
      this.formData.parking_space_ids = this.formData.parking_space_ids.filter(id => id !== spaceId)
    },

    async submit() {
      if (!this.$refs.form.validate()) {
        return
      }

      if (this.formData.parking_space_ids.length === 0) {
        this.error = 'Selecciona al menos un cajón'
        return
      }

      if (this.formData.beneficiary_company_id === this.getProfile.id_company) {
        this.error = 'No puedes seleccionar tu propia empresa como beneficiaria'
        return
      }

      this.loading = true
      this.error = null

      try {
        const dataToSend = {
          beneficiary_company_id: this.formData.beneficiary_company_id,
          parking_space_ids: this.formData.parking_space_ids,
          start_date: this.formData.start_date,
          end_date: this.formData.end_date,
          notes: this.formData.notes
        }

        if (this.formData.reserveByHours) {
          dataToSend.start_time = this.formData.start_time
          dataToSend.end_time = this.formData.end_time
        }

        await axios({
          method: 'post',
          url: 'https://empresa-service.intrare.app/Parking/invitations/reservations/create/',
          headers: {
            'Content-Type': 'application/json',
            'authorization': 'Token ' + store.getters.getToken
          },
          data: dataToSend
        })

        this.$emit('created')
      } catch (err) {
        // Mostrar en snackbar global
        handleApiError(err, store)
        // Mostrar también localmente en el dialog para máxima visibilidad
        this.error = extractErrorMessage(err)
      } finally {
        this.loading = false
      }
    },

    close() {
      this.$emit('close')
    }
  }
}
</script>

